import { FiatCurrenciesE, IBasePayload, IProfile, IResponsePagination } from 'primsell-shared-layer/types'

export enum CampaignStatusE {
  ACTIVE = 'active',
  DRAFT = 'draft',
  PENDING = 'pending',
  COMPLETED = 'completed',
}
export enum CodeTypeE {
  REDEEM_CODE = 'code',
  QR_CODE = 'qr_code',
  LOYALTY = 'loyalty',
}

export enum ImageSizeE {
  ORIGINAL = 'original',
  MEDIUM = 'medium',
  SMALL = 'small',
}

export interface IImageCropConfig {
  type: ImageSizeE
  width: number | null
}

export interface IImage extends IImageCropConfig {
  s3Path: string
  path: string
  mimetype: string
  type: ImageSizeE
}

export interface IEmailPreviewImage {
  id: string
  image: string
}

export interface ICampaign {
  id: string
  name: string | null
  code: string | null
  codeType: CodeTypeE | null
  createdAt: string | null
  currency: FiatCurrenciesE | null
  description: string | null
  startDate: string | null
  endDate: string | null
  eventDate: string | null
  images: IImage[] | null
  imageId: string | null
  isDraft: boolean
  redeemCodes?: string[] | null
  additionalRedeemCodes?: string[] | null
  itemsAvailable: number | null
  itemsBooked: number | null
  itemsBought: number | null
  onSale: boolean
  ownerId: string | null
  price: number | null
  senderEmail: string | null
  updatedAt: string | null
  inStock: number | null
  uploadedRedeemCodesCount: number | null
  eventLocation: string | null
  status: CampaignStatusE
  redeemEmailTemplateId: string | null
  purchaseEmailTemplateId: string | null
  contractAddress: string | null
  meta: {
    additionalDetails?: string | null
    eventLocationTimezoneOffset?: number
    eventLocationPlaceId?: string | null
    collectionName?: string | null
  }
  csvFile?: File | null
  file?: File | null
  redeemEmailTemplate?: IEmailPreviewImage | null
  purchaseEmailTemplate?: IEmailPreviewImage | null
}

export interface IActiveCampaignIdName {
  id: string
  name: string | null
}

export interface IRequestGetCampaignsListByProfileId {
  id: IProfile['id']
  pagination: Pick<IResponsePagination, 'limit' | 'offset'>
}

export type IGetActiveCampaignsIdNameListByProfileIdFilterProperties = 'codeType'

export interface IRequestGetActiveCampaignsIdNameListByProfileId
  extends IBasePayload<IGetActiveCampaignsIdNameListByProfileIdFilterProperties> {
  id: IProfile['id']
}

export interface IGetCodeResponse {
  code: string
}

export interface IUpdateQrCampaignRequest {
  startDate: ICampaign['startDate']
  endDate: ICampaign['endDate']
  itemsAvailable: ICampaign['itemsAvailable']
  currency: ICampaign['currency']
  price: ICampaign['price']
  eventLocation: ICampaign['eventLocation']
  eventDate: ICampaign['eventDate']
  meta: ICampaign['meta']
  senderEmail: ICampaign['senderEmail']
  redeemEmailTemplateId: ICampaign['redeemEmailTemplateId']
  purchaseEmailTemplateId: ICampaign['purchaseEmailTemplateId']
  name: ICampaign['name']
  description: ICampaign['description']
  imageId: ICampaign['imageId']
}

export interface IUpdateRedeemCampaignRequest {
  startDate: ICampaign['startDate']
  endDate: ICampaign['endDate']
  currency: ICampaign['currency']
  price: ICampaign['price']
  itemsAvailable: ICampaign['itemsAvailable']
  redeemCodes?: ICampaign['redeemCodes']
  additionalRedeemCodes?: ICampaign['additionalRedeemCodes']
  senderEmail: ICampaign['senderEmail']
  redeemEmailTemplateId: ICampaign['redeemEmailTemplateId']
  purchaseEmailTemplateId: ICampaign['purchaseEmailTemplateId']
  name: ICampaign['name']
  description: ICampaign['description']
  imageId: ICampaign['imageId']
}

export interface IUpdateLoyaltyCampaignRequest {
  name: ICampaign['name']
  description: ICampaign['description']
  startDate: ICampaign['startDate']
  endDate: ICampaign['endDate']
  itemsAvailable: ICampaign['itemsAvailable']
  currency: ICampaign['currency']
  price: ICampaign['price']
  senderEmail: ICampaign['senderEmail']
  code: ICampaign['code']
  imageId: ICampaign['imageId']
  isDraft: ICampaign['isDraft']
  onSale: ICampaign['onSale']
  codeType: ICampaign['codeType']
  purchaseEmailTemplateId: ICampaign['purchaseEmailTemplateId']
  meta: ICampaign['meta']
}

export interface IRequestPublicCampaignByCode {
  code: string
}

export enum CampaignMetaE {
  ADDITIONAL_DETAILS = 'additionalDetails',
  EVENT_LOCATION_TIMEZONE_OFFSET = 'eventLocationTimezoneOffset',
  EVENT_LOCATION_PLACE_ID = 'eventLocationPlaceId',
  COLLECTION_NAME = 'collectionName',
}

export interface IPublicCampaign {
  id: string
  code: string
  codeType: CodeTypeE
  status: string
  name: string | null
  description: string | null
  currency: FiatCurrenciesE | null
  price: number | null
  contractAddress: string | null
  inStock: number | null
  maxAmountPerPurchase: number | null
  startDate: string | null
  endDate: string | null
  images: IImage[]
  meta: Partial<Record<CampaignMetaE, string>>
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IRequestGetCampaignsForSelect
  extends IBasePayload<IGetActiveCampaignsIdNameListByProfileIdFilterProperties> {}

export interface ICampaignForSelect {
  id: ICampaign['id']
  name: ICampaign['name']
  codeType: ICampaign['codeType']
  startDate: ICampaign['startDate']
  itemsAvailable: ICampaign['itemsAvailable']
}
